/**
 * RevokeViaTokenPage — magic-link landing for /revoke-via-token/:token.
 *
 * Companion to ConsentLanding — same trust posture, but for the deletion
 * direction. Calls /api/profile-deletion-token-redeem with decision: "revoke"
 * or "decline_revoke".
 *
 * Per /policy/brand-discipline.md §11 dignity rules: the recipient is the
 * agent here. Plain language about what deletion means, what legal hold
 * means, no friction-by-design. Single primary action.
 */

import { useState } from "react";
import { useParams } from "react-router-dom";
import { Wordmark, Callout } from "../components/index.js";

export default function RevokeViaTokenPage() {
  const { token } = useParams();
  const [state, setState] = useState("ready"); // ready | submitting | revoked | declined | legal_hold | already_redeemed | expired | error
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const submit = async (decision) => {
    setState("submitting");
    setError(null);
    try {
      const res = await fetch("/.netlify/functions/profile-deletion-token-redeem", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ token, decision }),
      });
      const body = await res.json();
      if (!res.ok) {
        if (res.status === 410) {
          setState("expired");
        } else if (res.status === 409 && body.error === "legal_hold") {
          setState("legal_hold");
          setError(body.message);
        } else if (res.status === 409 && body.error === "already_redeemed") {
          setState("already_redeemed");
        } else {
          setState("error");
          setError(body.message || body.error || "Submission failed.");
        }
        return;
      }
      setResult(body);
      setState(decision === "revoke" ? "revoked" : "declined");
    } catch (e) {
      setState("error");
      setError(e.message);
    }
  };

  if (!token) {
    return (
      <Container>
        <ErrorState
          headline="Missing token in URL."
          body="Please use the exact link from the sender's email."
        />
      </Container>
    );
  }

  return (
    <Container>
      {state === "ready" && <ReadyView onRevoke={() => submit("revoke")} onDecline={() => submit("decline_revoke")} />}
      {state === "submitting" && <Loading />}
      {state === "revoked" && (
        <Outcome
          headline="Thank you. Your Profile is marked for deletion."
          body={
            result?.deletion_fires_at
              ? `Deletion fires on ${new Date(result.deletion_fires_at).toLocaleDateString()} (within 30 days), unless an active legal hold applies.`
              : "Deletion fires within 30 days, unless an active legal hold applies."
          }
        />
      )}
      {state === "declined" && (
        <Outcome
          headline="No changes made."
          body="Your Profile remains in place. You can use the same link to revoke later, but each link is single-use — ask the sender for a new one if you change your mind."
        />
      )}
      {state === "legal_hold" && (
        <ErrorState
          headline="Your Profile is under active legal hold."
          body={error || "Deletion cannot be processed at this time. Please contact the sender for the legal posture."}
        />
      )}
      {state === "already_redeemed" && (
        <ErrorState
          headline="This link has already been used."
          body="If you intended to revoke and the action didn't complete, contact the sender for a new link."
        />
      )}
      {state === "expired" && (
        <ErrorState
          headline="This revoke link has expired."
          body="Ask the sender to send a new one."
        />
      )}
      {state === "error" && (
        <ErrorState headline="Something isn't right with this link." body={error || "Please contact the sender."} />
      )}
    </Container>
  );
}

// ──────────────────────────────────────────────────────────────────────────

function Container({ children }) {
  return (
    <div style={{ maxWidth: 560, margin: "0 auto", padding: "32px 20px 80px" }}>
      <div style={{ marginBottom: 24 }}>
        <Wordmark scale={1.1} />
      </div>
      {children}
    </div>
  );
}

function ReadyView({ onRevoke, onDecline }) {
  return (
    <>
      <h1
        style={{
          fontFamily: "var(--ppr-typography-font-family-display)",
          fontSize: "var(--ppr-typography-font-size-3xl)",
          color: "var(--ppr-color-accent-navy)",
          letterSpacing: "var(--ppr-typography-letter-spacing-snug)",
          lineHeight: "var(--ppr-typography-line-height-tight)",
          margin: "0 0 12px",
        }}
      >
        Revoke your Interaction Engineering profile.
      </h1>
      <p
        style={{
          fontFamily: "var(--ppr-typography-font-family-body)",
          fontSize: "var(--ppr-typography-font-size-md)",
          lineHeight: "var(--ppr-typography-line-height-relaxed)",
          color: "var(--ppr-color-text-primary)",
          margin: "0 0 12px",
        }}
      >
        The sender sent you this link so you can remove your Profile from Interaction Engineering.
      </p>
      <p
        style={{
          fontFamily: "var(--ppr-typography-font-family-body)",
          fontSize: "var(--ppr-typography-font-size-base)",
          lineHeight: "var(--ppr-typography-line-height-relaxed)",
          color: "var(--ppr-color-text-secondary)",
          margin: "0 0 16px",
        }}
      >
        Interaction Engineering is a preparation tool people use to have better conversations. If you'd
        prefer to opt out, that's your call to make. Choose <strong>Revoke</strong> below.
      </p>

      <Callout.Tip>
        <strong>What "revoke" does:</strong> your Profile is marked for deletion and purged within 30 days. After that,
        managers will no longer see your Profile in Interaction Engineering.
      </Callout.Tip>

      <Callout.WatchOut>
        <strong>Legal hold exception:</strong> if your Profile is flagged for an active investigation
        or ongoing claim, deletion is blocked until the sender clears the hold. The sender controls
        that flag.
      </Callout.WatchOut>

      <div style={{ display: "flex", gap: 12, flexDirection: "column", marginTop: 24 }}>
        <PrimaryButton onClick={onRevoke}>Revoke — delete my Profile</PrimaryButton>
        <SecondaryButton onClick={onDecline}>Keep my Profile in place</SecondaryButton>
      </div>

      <p
        style={{
          marginTop: 24,
          fontFamily: "var(--ppr-typography-font-family-mono)",
          fontSize: "var(--ppr-typography-font-size-xs)",
          color: "var(--ppr-color-text-muted)",
        }}
      >
        This link is single-use. Whichever button you tap below will use it.
      </p>
    </>
  );
}

function Loading() {
  return (
    <p
      style={{
        fontFamily: "var(--ppr-typography-font-family-mono)",
        fontSize: "var(--ppr-typography-font-size-sm)",
        color: "var(--ppr-color-text-muted)",
      }}
    >
      Submitting…
    </p>
  );
}

function ErrorState({ headline, body }) {
  return (
    <div>
      <h1
        style={{
          fontFamily: "var(--ppr-typography-font-family-display)",
          fontSize: "var(--ppr-typography-font-size-2xl)",
          color: "var(--ppr-color-accent-coral-dark)",
          margin: "0 0 12px",
        }}
      >
        {headline}
      </h1>
      <p
        style={{
          fontFamily: "var(--ppr-typography-font-family-body)",
          fontSize: "var(--ppr-typography-font-size-base)",
          lineHeight: "var(--ppr-typography-line-height-relaxed)",
          color: "var(--ppr-color-text-secondary)",
        }}
      >
        {body}
      </p>
    </div>
  );
}

function Outcome({ headline, body }) {
  return (
    <div>
      <h1
        style={{
          fontFamily: "var(--ppr-typography-font-family-display)",
          fontSize: "var(--ppr-typography-font-size-2xl)",
          color: "var(--ppr-color-accent-navy)",
          letterSpacing: "var(--ppr-typography-letter-spacing-snug)",
          margin: "0 0 12px",
        }}
      >
        {headline}
      </h1>
      <p
        style={{
          fontFamily: "var(--ppr-typography-font-family-body)",
          fontSize: "var(--ppr-typography-font-size-base)",
          lineHeight: "var(--ppr-typography-line-height-relaxed)",
          color: "var(--ppr-color-text-secondary)",
        }}
      >
        {body}
      </p>
    </div>
  );
}

function PrimaryButton({ onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "14px 18px",
        fontFamily: "var(--ppr-typography-font-family-display)",
        fontSize: "var(--ppr-typography-font-size-sm)",
        letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
        textTransform: "uppercase",
        border: "1px solid var(--ppr-color-border-default)",
        background: "var(--ppr-color-accent-navy)",
        color: "var(--ppr-color-text-inverse)",
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

function SecondaryButton({ onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "14px 18px",
        fontFamily: "var(--ppr-typography-font-family-display)",
        fontSize: "var(--ppr-typography-font-size-sm)",
        letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
        textTransform: "uppercase",
        border: "1px solid var(--ppr-color-border-default)",
        background: "var(--ppr-color-background-surface)",
        color: "var(--ppr-color-accent-navy)",
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}
